import { useTranslation } from 'react-i18next';
import { History, Lock } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { Button } from '@/components/ui/button';
import { useLocale } from '@/lib/locale';
import type { OmlHistoryRow } from '../../../../shared/types/oml';
import { entityLabel, fieldLabel } from './oml-ui';

const fmt = (v: unknown): string => {
  if (v === null || v === undefined || v === '') return '—';
  if (Array.isArray(v)) return v.join(' · ');
  if (typeof v === 'boolean') return v ? 'Sí' : 'No'; // i18n-ok: valor del historial, idioma del contenido (§7.3)
  const s = String(v);
  return s.length > 140 ? `${s.slice(0, 140)}…` : s;
};

/**
 * Historial append-only (§8.5, AC-20/21): quién cambió qué y cuándo, con valor
 * anterior y nuevo; sobre restringidos, solo quien puede ver el campo ve los
 * valores. Sin ninguna acción de edición ni borrado.
 */
export function HistoryPanel({ accountId, entityId, names, limit = 100 }: { accountId: string; entityId?: string; names?: Record<string, string>; limit?: number }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const q = trpc.oml.history.list.useInfiniteQuery({ accountId, entityId, limit }, { getNextPageParam: (last) => last.cursor ?? undefined, initialCursor: null });
  const rows = (q.data?.pages.flatMap((p) => p.items) ?? []) as (OmlHistoryRow & { hidden?: boolean })[];
  if (q.isLoading) return <div className="flex justify-center py-6"><WinclapLoader size={28} className="text-foreground/25" /></div>;
  if (!rows.length) return <p className="text-sm text-muted-foreground py-4">{t('oml.history.empty', 'Todavía no hay cambios registrados.')}</p>;
  const who = (e: string) => names?.[e.toLowerCase()] ?? e;
  return (
    <div className="space-y-2">
      <ul className="divide-y rounded-md border">
        {rows.map((h) => (
          <li key={h.id} className="px-3 py-2 text-sm">
            <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
              <span><History className="inline h-3 w-3 mr-1" />{new Date(h.at).toLocaleString(locale)} · {who(h.by)} · {t(`oml.roles.${h.byRole}`, h.byRole)}</span>
              <span>{entityLabel(t, h.entityType)}{h.entityName ? ` · ${h.entityName}` : ''}</span>
            </div>
            <div className="mt-0.5">
              {h.kind === 'create' && h.field === '*' ? (
                <span>{t('oml.history.created', 'Creado')}</span>
              ) : h.kind === 'archive' ? (
                <span>{t('oml.history.archived', 'Archivado')}</span>
              ) : h.kind === 'restore' ? (
                <span>{t('oml.history.restored', 'Restaurado')}</span>
              ) : h.kind === 'delete' ? (
                <span>{t('oml.history.deleted', 'Eliminado')}</span>
              ) : (
                <span>
                  <span className="font-medium">{h.field === '*' ? entityLabel(t, h.entityType) : fieldLabel(t, h.entityType, h.field)}</span>
                  {h.hidden || (h.restricted && h.from === undefined && h.to === undefined) ? (
                    <span className="ml-2 inline-flex items-center gap-1 text-muted-foreground"><Lock className="h-3 w-3" />{t('oml.history.restricted', 'cambio sobre un campo restringido (valores visibles solo con permiso)')}</span>
                  ) : (
                    <span className="ml-2"><span className="line-through text-muted-foreground">{fmt(h.from)}</span> → <span>{fmt(h.to)}</span></span>
                  )}
                </span>
              )}
              {h.justification && <div className="text-xs text-muted-foreground mt-0.5">{t('oml.history.justification', 'Justificación')}: {h.justification}</div>}
              {h.secretWarningConfirmed && <div className="text-xs text-amber-700 mt-0.5">{t('oml.history.secret_confirmed', 'Confirmó que "Dónde está la credencial" no es una clave.')}</div>}
            </div>
          </li>
        ))}
      </ul>
      {q.hasNextPage && <Button variant="outline" size="sm" onClick={() => q.fetchNextPage()} disabled={q.isFetchingNextPage}>{t('common.load_more', 'Cargar más')}</Button>}
    </div>
  );
}
